import { BaseQueryApi } from '@reduxjs/toolkit/dist/query';

import configSelector from '@/features/config/selectors/config';
import flattenFilterGridItemsSelector from '@/features/config/selectors/flattenFilterGridItems';
import type { RootState } from '@/features/store';
import { FilterBlockType } from '@/interfaces/constructor.interfaces';
import {
  FiltersRawItem,
  IExtendedFilterCollectionItem,
} from '@/interfaces/filters.interfaces';
import { IReference } from '@/interfaces/grpc.interfaces';
import { ClusterItem, LocationType } from '@/interfaces/locations';
import { IGrpcService } from '@/services/GrpcService';
import injectContainer from '@/services/InjectContainer';
import ServiceTypes from '@/services/ServiceTypes';
import deepCopy from '@/utils/deepCopy';
import instanceOfReference from '@/utils/instanceOfReference';

import { FiltersParameters } from '../types';

/**
 * Запрос на получение дополнительных значений фильтров
 * @param api
 * @param filtersRawItem {object} Данные по фильтрам
 * @returns Данные по фильтрам с дополнительными значениями
 */
const fetchFiltersWithExtraValues = async (
  api: BaseQueryApi,
  filtersRawItem: FiltersRawItem,
) => {
  const state = api.getState() as RootState;

  const config = configSelector(state);
  const filterGridItems = flattenFilterGridItemsSelector(state);

  const grpcService = injectContainer.get<IGrpcService>(ServiceTypes.Grpc);
  const { itemsClient } = grpcService;

  const filtersRawWithExtraValuesItem = deepCopy(filtersRawItem);

  const { data } = filtersRawWithExtraValuesItem;

  const promises: Array<Promise<void>> = [];

  for (const [key, values] of Object.entries(data)) {
    const filterGridItem = filterGridItems.find(item => item.key === key);

    if (!filterGridItem) {
      continue;
    }

    switch (filterGridItem.type) {
      case FilterBlockType.FilterLocation: {
        if (!Array.isArray(values) || !values.length) {
          break;
        }

        if (!instanceOfReference(values[0])) {
          break;
        }

        const filterLocationValues = values as IExtendedFilterCollectionItem[];

        const clusters = filterLocationValues.reduce<IReference[]>((acc, value) => {
          if (instanceOfReference(value.cluster)) {
            return [...acc, value.cluster];
          }

          return acc;
        }, []);

        if (!clusters.length) {
          break;
        }

        const clusterParameters: FiltersParameters = {
          spaceId: config.spaceId,
          envId: config.envId,
          collectionId: clusters[0].collection_id,
          filter: {
            id: clusters.map(c => c.id),
          },
        };

        const locationPromise = itemsClient
          .findPublished(clusterParameters)
          .then(({ items }) => {
            const formattedItems = (
              items as ClusterItem[]
            ).map<IExtendedFilterCollectionItem>(item => {
              return {
                id: item.id,
                name: item.data.name,
                displayName: item.data.name,
                position: item.data.position,
                available: true,
                disabled: false,
                collection_id: item.collectionId,
                type: LocationType.Cluster,
              };
            });
            filterLocationValues.unshift(...formattedItems);
          });

        promises.push(locationPromise);

        break;
      }

      default: {
        break;
      }
    }
  }

  await Promise.all(promises);

  return filtersRawWithExtraValuesItem;
};

export default fetchFiltersWithExtraValues;
